import { Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { BaseModalComponent } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Asset } from '@app/models/asset/asset';
import { ProductionCardBundle, ProductionCardBundleStatus } from '@app/models/production/production-card/production-card-bundle';
import { AssetProxyService } from '@app/proxies/asset/asset.proxy.service';
import { ProductionCardBundleProxyService } from '@app/proxies/production/production-card-bundle.proxy.service';

export interface ChangeDyeingMachineResult {
  selectedAsset: Asset;
}

export interface BundleDisplayInfo {
  bundle: ProductionCardBundle;
  status: 'weighed' | 'with-rolls' | 'empty';
  statusLabel: string;
  canDelete: boolean;
  rollsWeight: number;
}

@Component({
  selector: 'app-change-dyeing-machine-modal',
  templateUrl: './change-dyeing-machine-modal.component.html',
  styleUrl: './change-dyeing-machine-modal.component.scss',
})
export class ChangeDyeingMachineModalComponent extends BaseModalComponent<ChangeDyeingMachineResult | null> implements OnInit {
  private readonly assetProxyService = inject(AssetProxyService);
  private readonly productionCardBundleProxyService = inject(ProductionCardBundleProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);

  @Input() currentAsset: Asset | null = null;
  @Input() minNumberOfPorts = 0;
  @Input() set bundles(value: ProductionCardBundle[]) {
    this._bundles.set(value);
  }
  get bundles(): ProductionCardBundle[] {
    return this._bundles();
  }
  @Input() totalAssignedLbs = 0;
  @Input() productionCardStatus = 0;
  @Input() onBundleDeleted: (() => void) | null = null;

  private _bundles = signal<ProductionCardBundle[]>([]);
  availableMachines = signal<Asset[]>([]);
  isLoading = signal<boolean>(false);
  isDeletingBundle = signal<boolean>(false);

  selectedMachineControl = new FormControl<Asset | null>(null, Validators.required);

  bundleDisplayList = computed<BundleDisplayInfo[]>(() => {
    return this._bundles().map((bundle) => {
      const hasRolls = bundle.rollNumbers && bundle.rollNumbers.length > 0;
      const isWeighed = (bundle.actualWeightInLbs && bundle.actualWeightInLbs > 0) || bundle.status === ProductionCardBundleStatus.Weighed;
      const rollsWeight =
        bundle.rolls?.reduce((sum, roll) => {
          const weight =
            roll.stationTimeStamps?.sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime())[0]?.weightInLbs ??
            roll.plannedWeight ??
            0;
          return sum + weight;
        }, 0) ?? 0;

      let status: 'weighed' | 'with-rolls' | 'empty';
      let statusLabel: string;
      let canDelete: boolean;

      if (isWeighed) {
        status = 'weighed';
        statusLabel = this.translateWithFallback('Weighed', 'Weighed');
        canDelete = false;
      } else if (hasRolls) {
        status = 'with-rolls';
        statusLabel = this.translateWithFallback('With_Rolls_Not_Weighed', 'With Rolls (Not Weighed)');
        canDelete = false;
      } else {
        status = 'empty';
        statusLabel = this.translateWithFallback('Empty', 'Empty');
        canDelete = this.productionCardStatus <= 7; // Processing or before BRD
      }

      return { bundle, status, statusLabel, canDelete, rollsWeight };
    });
  });

  // Filter machines based on actual assigned lbs capacity and minimum ports
  eligibleMachines = computed(() => {
    const minPorts = this.nonDeletableBundlesCount();
    return this.availableMachines().filter((machine) => {
      const capacityLbs = this.getCapacityInLbs(machine);
      const numberOfPorts = machine.dyeingMachineDetails?.numberOfPorts ?? 0;
      // Machine must have enough capacity for assigned rolls and enough ports for bundles with rolls
      return capacityLbs >= this.totalAssignedLbs && numberOfPorts >= minPorts && machine.id !== this.currentAsset?.id;
    });
  });

  // Count bundles that cannot be deleted (have rolls or are weighed)
  nonDeletableBundlesCount = computed(() => {
    return this.bundleDisplayList().filter((b) => !b.canDelete).length;
  });

  // Check if there are any weighed bundles
  hasWeighedBundles = computed(() => {
    return this.bundleDisplayList().some((b) => b.status === 'weighed');
  });

  // Count bundles that will be deleted based on selected machine
  getBundlesToDeleteCount(): number {
    const selectedMachine = this.selectedMachineControl.value;
    if (!selectedMachine) return 0;

    const newPorts = selectedMachine.dyeingMachineDetails?.numberOfPorts ?? 0;
    const totalBundles = this._bundles().length;
    const deletableBundles = this.bundleDisplayList().filter((b) => b.canDelete).length;

    // How many bundles need to be removed to match new port count
    const excessBundles = totalBundles - newPorts;

    // Can only delete up to the number of deletable bundles
    return Math.max(0, Math.min(excessBundles, deletableBundles));
  }

  ngOnInit(): void {
    this.loadDyeingMachines();
  }

  private loadDyeingMachines(): void {
    this.isLoading.set(true);
    this.assetProxyService.getAllMachineOptionCorrectWay().subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          this.availableMachines.set(response.data.items);
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading dyeing machines:', error);
        this.isLoading.set(false);
      },
    });
  }

  getPortCapacity(machine: Asset): number {
    return machine.assetCapacity?.maxCapacity ?? 0;
  }

  // Convert kg to lbs (1 kg = 2.20462 lbs)
  getCapacityInLbs(machine: Asset): number {
    const capacityInKg = machine.assetCapacity?.maxCapacity ?? 0;
    return capacityInKg * 2.20462;
  }

  onSave(): void {
    if (this.selectedMachineControl.invalid || !this.selectedMachineControl.value) {
      this.selectedMachineControl.markAsTouched();
      return;
    }

    const selectedMachine = this.selectedMachineControl.value;
    const bundlesToDelete = this.getBundlesToDeleteCount();

    if (bundlesToDelete > 0) {
      const message = this.translateWithFallback(
        'Change_Dyeing_Machine_Modal_Confirm_Change_With_Delete',
        `${bundlesToDelete} empty bundle(s) will be automatically deleted. Are you sure you want to change the machine?`,
      ).replace('{{count}}', bundlesToDelete.toString());
      const header = this.translateWithFallback('Change_Dyeing_Machine_Modal_Confirm_Change', 'Confirm Change');

      this.confirmationService.confirm({
        message,
        header,
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translateWithFallback('Yes', 'Yes'),
        rejectLabel: this.translateWithFallback('Cancel', 'Cancel'),
        acceptButtonStyleClass: 'p-button-success',
        rejectButtonStyleClass: 'p-button-danger',
        accept: () => this.completeChangeMachine(selectedMachine),
      });
    } else {
      this.completeChangeMachine(selectedMachine);
    }
  }

  private completeChangeMachine(selectedMachine: Asset): void {
    const result: ChangeDyeingMachineResult = {
      selectedAsset: selectedMachine,
    };

    this.close({ action: 'save', data: result });
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }

  confirmDeleteBundle(bundleInfo: BundleDisplayInfo): void {
    if (!bundleInfo.canDelete) {
      return;
    }

    const message = this.translateWithFallback(
      'Change_Dyeing_Machine_Modal_Confirm_Delete_Bundle',
      `Are you sure you want to delete bundle ${bundleInfo.bundle.number}?`,
    ).replace('{{number}}', bundleInfo.bundle.number?.toString() ?? '');
    const header = this.translateWithFallback('Change_Dyeing_Machine_Modal_Confirm_Delete', 'Confirm Delete');

    this.confirmationService.confirm({
      message,
      header,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateWithFallback('Yes', 'Yes'),
      rejectLabel: this.translateWithFallback('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteBundle(bundleInfo.bundle),
    });
  }

  private deleteBundle(bundle: ProductionCardBundle): void {
    if (!bundle.id) {
      return;
    }

    this.isDeletingBundle.set(true);
    this.productionCardBundleProxyService.delete(bundle.id).subscribe({
      next: (response) => {
        this.isDeletingBundle.set(false);
        if (response.success) {
          this._bundles.update((bundles) => bundles.filter((b) => b.id !== bundle.id));
          const title = this.translateWithFallback('Change_Dyeing_Machine_Modal_Bundle_Deleted', 'Bundle Deleted');
          const message = this.translateWithFallback(
            'Change_Dyeing_Machine_Modal_Bundle_Deleted_Message',
            `Bundle ${bundle.number} has been deleted.`,
          ).replace('{{number}}', bundle.number?.toString() ?? '');
          GlobalMessageUtil.success(title, message);
          if (this.onBundleDeleted) {
            this.onBundleDeleted();
          }
        }
      },
      error: () => {
        this.isDeletingBundle.set(false);
      },
    });
  }

  private translateWithFallback(key: string, fallback: string): string {
    const translation = this.translate.instant(key);
    return translation === key ? fallback : translation;
  }
}
